'use client';

import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import React from 'react';

import { getFormContact } from '@/app/[locale]/(default-layout)/(forms)/_components/form-contact-helper';
import {
    resolveFormPageText,
    resolveFormPageTitle,
    resolveFormImageUrl
} from '@/app/[locale]/(default-layout)/(forms)/_components/form-image-helper';
import FormContactLinks from '@/app/[locale]/(default-layout)/(forms)/_components/FormContactLinks';
import RentACinemaForm from '@/app/[locale]/(default-layout)/(forms)/_components/RentACinemaForm';

import { ID_INNENSTADT_KINOS, ID_UFA_DUESSELDORF } from '@cineamo/api/constants/cinema-ids';

import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

import CinemaDescriptionMarkdown from '@cineamo/ui/src/markdown/CinemaDescriptionMarkdown';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

const CinemaScreenImage = loadAsset('/images/placeholders/im-cinemaScreen.png');
const RentACinemaImageInnenstadtKinos = 'https://cdn.cineamo.com/tmp/cinema-778-innenstadtkinos/rentcinema2.jpg';
const RentACinemaImageUfaDueseldorf =
    'https://cdn.cineamo.com/cinema-card-image/d6556406-4436-4868-bdf3-0b1714c9a1ac.jpg';

type RentACinemaPageClientProps = {
    /** Fallback hero image (first cinema room picture), used when no image is configured. */
    hallImageUrl?: string;
};

const RentACinemaPageClient: React.FC<RentACinemaPageClientProps> = ({ hallImageUrl }) => {
    const t = useTranslations();
    const isMounted = useIsMounted();
    const locale = useLocale();
    const domainConfiguration = useDomainConfiguration();
    const cinema = domainConfiguration.cinema;

    const { phone, phoneURI, email } = getFormContact(domainConfiguration, 'rentACinema');

    let screenImageOverride;
    switch (cinema?.id) {
        case ID_UFA_DUESSELDORF:
            screenImageOverride = RentACinemaImageUfaDueseldorf;
            break;
        case ID_INNENSTADT_KINOS:
            screenImageOverride = RentACinemaImageInnenstadtKinos;
            break;
    }

    // Custom image configured on the cinema app's "our services" forms section takes
    // precedence, then the cinema-specific hardcoded override above, then the hall picture.
    const heroImage =
        resolveFormImageUrl(domainConfiguration, 'rentACinema', hallImageUrl) ||
        screenImageOverride ||
        CinemaScreenImage;

    let pageTitleOverride;
    if (cinema.id === ID_UFA_DUESSELDORF) {
        pageTitleOverride = locale === 'de' ? 'Ein ganzer Kinosaal nur für Sie' : null;
    }

    let pageDescriptionOverride;
    if (cinema.id === ID_UFA_DUESSELDORF) {
        pageDescriptionOverride =
            locale === 'de'
                ? 'Mit einem Hauch von Hollywood. In unseren Kinosälen gestalten Sie Ihr Event in einem einzigartigen Ambiente. Egal ob Firmenfeier, Weihnachtsfeier, Präsentation, Meeting oder ein anderes Business-Event: Wir erstellen für Sie ein individuelles Angebot.'
                : null;
    }

    const customDescription = resolveFormPageText(domainConfiguration, 'rentACinema');
    const customPageTitle = resolveFormPageTitle(domainConfiguration, 'rentACinema');
    const description = pageDescriptionOverride || t('rent-a-cinema.content.description');

    let benefitsTitleOverride;
    if (cinema.id === ID_UFA_DUESSELDORF) {
        benefitsTitleOverride = locale === 'de' ? 'Filmreife Location für Ihr Event' : null;
    }

    let benefitsListOverride;
    if (cinema.id === ID_UFA_DUESSELDORF) {
        benefitsListOverride =
            locale === 'de'
                ? [
                      'Verschiedene Kinosäle mit unterschiedlichen Kapazitäten',
                      t('rent-a-cinema.benefits.flexible'),
                      'Kinosnacks & Getränke (oder Catering über externe Anbieter)',
                      'Aktuelle Filme, Klassiker (je nach Verfügbarkeit) oder eigene Inhalte'
                  ]
                : null;
    }

    const benefitsList = benefitsListOverride || [
        t('rent-a-cinema.benefits.exclusive'),
        t('rent-a-cinema.benefits.flexible'),
        t('rent-a-cinema.benefits.catering'),
        t('rent-a-cinema.benefits.technical')
    ];
    return (
        <main className="min-h-screen">
            {/* Main Content */}
            <section className="py-24">
                <CenterLayout>
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Column - Content */}
                        <div className="space-y-12">
                            <div className="space-y-6">
                                <h1 className="text-headline-32 text-[var(--on-background-color)]">
                                    {customPageTitle || pageTitleOverride || t('rent-a-cinema.content.title')}
                                </h1>
                                <div className="text-body-16 text-[rgba(var(--on-background-color-rgb),0.8)]">
                                    {customDescription ? (
                                        <CinemaDescriptionMarkdown
                                            description={customDescription}
                                            allowInlineHtml
                                        />
                                    ) : (
                                        <p>{description}</p>
                                    )}
                                </div>
                            </div>

                            {/* Benefits list is part of the default copy, so a custom page text
                                replaces it too — otherwise the operator's own wording would sit
                                above a boilerplate list they can neither edit nor remove. */}
                            {!customDescription && (
                                <div className="space-y-6">
                                    <h3 className="text-headline-20 font-semibold text-[var(--on-background-color)]">
                                        {benefitsTitleOverride || t('rent-a-cinema.benefits.title')}
                                    </h3>
                                    <ul className="space-y-4">
                                        {benefitsList.map((benefit, index) => (
                                            <li
                                                className="flex items-center space-x-3"
                                                key={`benefit-${index}`}>
                                                <div className="size-[16px] rounded-full flex items-center justify-center flex-shrink-0 outline-none bg-[var(--primary-color)]">
                                                    <span className="text-12 text-[var(--on-primary-color)]">✓</span>
                                                </div>
                                                <span className="text-body-14 text-[rgba(var(--on-background-color-rgb),0.8)]">
                                                    {benefit}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Right Column - Image */}
                        <div className="relative aspect-[16/9] rounded-12 overflow-hidden">
                            {isMounted && (
                                <Image
                                    src={heroImage}
                                    alt=""
                                    fill
                                    className="object-cover"
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                />
                            )}
                        </div>
                    </div>
                </CenterLayout>
            </section>

            {/* Request Form Section */}
            <section className="py-24">
                <CenterLayout>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-16">
                            <h2 className="text-headline-32 text-[var(--on-background-color)]">
                                {t('rent-a-cinema.form.title')}
                            </h2>
                        </div>

                        <RentACinemaForm />
                    </div>
                </CenterLayout>
            </section>

            {/* Contact Section */}
            <section className="mt-48 py-24 backdrop-blur-sm bg-[rgba(var(--on-background-color-rgb),0.05)]">
                <CenterLayout>
                    <div className="text-center space-y-8">
                        <h2 className="text-headline-32 text-[var(--on-background-color)]">
                            {t('rent-a-cinema.contact.question')}
                        </h2>
                        <p className="text-body-16 max-w-2xl mx-auto text-[rgba(var(--on-background-color-rgb),0.8)]">
                            {t('rent-a-cinema.contact.help-text')}
                        </p>
                        <FormContactLinks
                            namespace="rent-a-cinema"
                            phone={phone}
                            phoneURI={phoneURI}
                            email={email}
                        />
                    </div>
                </CenterLayout>
            </section>
        </main>
    );
};

export default RentACinemaPageClient;
